import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { ReadyState } from 'react-use-websocket';
// auth
import { useAuthContext } from 'src/auth/hooks';
// hooks
import { useWsConnection, useWsHandlers } from './hooks';
// context
import WsContext from './ws-context';
// types
import type { WsProviderProps, WsContextValue } from './types';
// redux
import { useDispatch } from 'src/redux/store';
import { updateUsers, updateRoles, updateAccesses } from 'src/redux/slice/user';
import { addVoucher } from 'src/redux/slice/voucher';
import { updateInitCachePartial } from 'src/utils/indexeddb-cache';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_WS_URL = import.meta.env.VITE_WS_URL || '';
const DEFAULT_RECONNECT_INTERVAL = 3000;
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 0; // unlimited: a POS must keep reconnecting all day
const DEFAULT_HEARTBEAT_INTERVAL = 30000;

// ============================================================================
// Helpers
// ============================================================================

function getSelectedConfigId(configSelected: any): string | undefined {
  if (!configSelected) return undefined;
  if (typeof configSelected === 'string') return configSelected;
  return configSelected.id || configSelected._id || configSelected.config;
}

// ============================================================================
// Provider
// ============================================================================

function WsProvider({
  children,
  url = DEFAULT_WS_URL,
  reconnectInterval = DEFAULT_RECONNECT_INTERVAL,
  maxReconnectAttempts = DEFAULT_MAX_RECONNECT_ATTEMPTS,
  heartbeatInterval = DEFAULT_HEARTBEAT_INTERVAL,
}: WsProviderProps) {
  // ---- Client key ----
  const [clientKey, setClientKey] = useState<string | null>(() => {
    if (typeof window !== 'undefined') return window.localStorage.getItem('WS-CLIENT-KEY');
    return null;
  });

  // ---- Last WS message timestamp (exposed in context for SyncIndicator) ----
  const [lastActivityAt, setLastActivityAt] = useState<Date | null>(null);
  const lastActivityRef = useRef<Date | null>(null);

  // ---- Auth ----
  const { user } = useAuthContext();

  // ---- Redux dispatch ----
  const dispatch = useDispatch();

  // ---- Selected config ----
  const selectedConfigId = useMemo(() => getSelectedConfigId(user?.configSelected), [user?.configSelected]);

  // ---- Message handlers ----
  const { handleMessage, handleAuth, handleLogout } = useWsHandlers({
    onClientKeyChange: setClientKey,
  });

  // --------------------------------------------------------------------------
  // INIT-DATA LISTENERS
  // --------------------------------------------------------------------------
  useEffect(() => {
    const handleInitComplete = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { roles, accesses, entity, data: streamedData } = customEvent.detail || {};

      if (roles && Array.isArray(roles)) {
        dispatch(updateRoles({ data: roles }));
      }
      if (accesses && Array.isArray(accesses)) {
        dispatch(updateAccesses({ data: accesses }));
      }
      if (entity === 'products' && streamedData && Array.isArray(streamedData)) {
        await updateInitCachePartial('products', streamedData);
      }
      if (entity === 'users' && streamedData && Array.isArray(streamedData)) {
        dispatch(updateUsers({ data: streamedData }));
        await updateInitCachePartial('users', streamedData);
      }
    };

    window.addEventListener('ws:init-complete', handleInitComplete);
    window.addEventListener('ws:init-data-complete', handleInitComplete);
    return () => {
      window.removeEventListener('ws:init-complete', handleInitComplete);
      window.removeEventListener('ws:init-data-complete', handleInitComplete);
    };
  }, [dispatch]);

  // --------------------------------------------------------------------------
  // RETURN EVENTS – triggers invoice refresh chain
  // --------------------------------------------------------------------------
  useEffect(() => {
    const patchActivity = () => {
      const now = new Date();
      lastActivityRef.current = now;
      setLastActivityAt(now);
    };

    const handleReturnEvent = (_event: Event) => {
      patchActivity();
      window.dispatchEvent(new CustomEvent('ws:refresh-invoices'));
    };

    window.addEventListener('ws:return-created', handleReturnEvent as EventListener);
    window.addEventListener('ws:return-updated', handleReturnEvent as EventListener);
    return () => {
      window.removeEventListener('ws:return-created', handleReturnEvent as EventListener);
      window.removeEventListener('ws:return-updated', handleReturnEvent as EventListener);
    };
  }, []);

  // --------------------------------------------------------------------------
  // VOUCHER EVENTS — Redux updates + telemetry
  // --------------------------------------------------------------------------
  const voucherCreatedHandler = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent;
      const { voucher } = customEvent.detail || {};
      if (voucher?.id) {
        dispatch(addVoucher(voucher));
        console.info('[WsProvider] Voucher added to Redux store:', voucher.code);
      }
    },
    [dispatch],
  );

  const voucherUpdatedHandler = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent;
      const { id, remainingAmount, status } = customEvent.detail || {};
      if (id && remainingAmount !== undefined) {
        dispatch(addVoucher({ id, remainingAmount, status }));
        console.info('[WsProvider] Voucher updated in Redux store:', id);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const patchActivity = () => {
      const now = new Date();
      lastActivityRef.current = now;
      setLastActivityAt(now);
    };

    const wrap = (fn: EventListener) => {
      return ((ev: Event) => {
        patchActivity();
        fn(ev);
      }) as EventListener;
    };

    window.addEventListener('ws:voucher-created', wrap(voucherCreatedHandler) as EventListener);
    window.addEventListener('ws:voucher-updated', wrap(voucherUpdatedHandler) as EventListener);
    return () => {
      window.removeEventListener('ws:voucher-created', wrap(voucherCreatedHandler) as EventListener);
      window.removeEventListener('ws:voucher-updated', wrap(voucherUpdatedHandler) as EventListener);
    };
  }, [voucherCreatedHandler, voucherUpdatedHandler]);

  // --------------------------------------------------------------------------
  // WebSocket connection
  // --------------------------------------------------------------------------
  const {
    sendMessage,
    lastMessage,
    readyState,
    connectionStatus,
    connect,
    disconnect,
    reconnect,
    wsLatencyMs,
    lastPongAt,
    missedHeartbeats,
    reconnectCount,
  } = useWsConnection({
    url,
    reconnectInterval,
    maxReconnectAttempts,
    heartbeatInterval,
    // Patch activity timestamp on every received message for SyncIndicator
    onMessage: (message: any) => {
      // Update last activity for telemetry
      if (message?.type) {
        const now = new Date();
        lastActivityRef.current = now;
        setLastActivityAt(now);
      }
      // Delegate to business-logic handler
      handleMessage(message);
    },
    onConnect: () => console.info('[WebSocket] Connected successfully'),
    onDisconnect: () => console.info('[WebSocket] Disconnected'),
    onError: (error) => console.error('[WebSocket] Connection error:', error),
  });

  // --------------------------------------------------------------------------
  // RECONNECTION — post-connect completeness check (debounced)
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (connectionStatus === 'connected' && user?.id && user?.configSelected?._id) {
      const timer = setTimeout(async () => {
        try {
          const { checkMissingData } = await import('src/api/server/init');
          const result = await checkMissingData(user.id, user.configSelected._id);
          if (!result.hasAllData) {
            console.info('[WsProvider] Re-connected, missing data:', result.missing);
          } else {
            console.info('[WsProvider] Re-connected — all data cached ✓');
          }
        } catch (err) {
          console.warn('[WsProvider] Post-reconnect data check failed:', err);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [connectionStatus, user]);

  // --------------------------------------------------------------------------
  // AUTH — send auth message when WS connection opens
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      if (user) {
        const authMessage = handleAuth(user, clientKey, selectedConfigId);
        sendMessage(authMessage);
      } else {
        sendMessage(handleLogout(clientKey));
      }
    }
  }, [readyState, user, clientKey, selectedConfigId, sendMessage, handleAuth, handleLogout]);

  // --------------------------------------------------------------------------
  // CONFIG_SELECTED
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (readyState === ReadyState.OPEN && selectedConfigId) {
      sendMessage({ type: 'config_selected', configSelected: selectedConfigId });
      console.info('[WsProvider] Config selected sent:', selectedConfigId);
    }
  }, [readyState, selectedConfigId, sendMessage]);

  // --------------------------------------------------------------------------
  // Context value
  // --------------------------------------------------------------------------
  const contextValue = useMemo<WsContextValue>(
    () => ({
      wsConnected: connectionStatus === 'connected',
      connectionStatus,
      readyState,
      sendMessage,
      lastMessage,
      connect,
      disconnect,
      reconnect,
      clientKey,
      lastActivityAt,
      wsLatencyMs,
      lastPongAt,
      missedHeartbeats,
      reconnectCount,
    }),
    [
      connectionStatus,
      readyState,
      sendMessage,
      lastMessage,
      connect,
      disconnect,
      reconnect,
      clientKey,
      lastActivityAt,
      wsLatencyMs,
      lastPongAt,
      missedHeartbeats,
      reconnectCount,
    ],
  );

  return <WsContext.Provider value={contextValue}>{children}</WsContext.Provider>;
}

export default WsProvider;
